import PageHeader from "../components/layout/PageHeader";
import { TID } from "../lib/testIds";
import { useEffect, useState } from "react";
import { api, apiErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import ChangePasswordCard from "../components/ChangePasswordCard";

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    n8n_base_url: "",
    n8n_api_key: "",
    n8n_webhook_url: "",
    krai_api_base_url: "",
    krai_api_token: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);
  async function load() {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
      setForm({
        n8n_base_url: data.n8n_base_url || "",
        n8n_api_key: "",
        n8n_webhook_url: data.n8n_webhook_url || "",
        krai_api_base_url: data.krai_api_base_url || "",
        krai_api_token: "",
      });
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/settings", form);
      setSettings(data);
      setForm((f) => ({ ...f, n8n_api_key: "", krai_api_token: "" }));
      toast.success("Configuración guardada");
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setSaving(false); }
  }

  return (
    <div data-testid={TID.settings.root}>
      <PageHeader
        eyebrow="Preferencias"
        title="Configuración"
        description="Parámetros globales de KRAI CONNECT. Todos los cambios se guardan en un fichero JSON local."
      />

      <form onSubmit={save} className="space-y-8 max-w-3xl">
        <Section title="n8n">
          <Field label="Base URL">
            <Input
              data-testid={TID.settings.n8nBase}
              value={form.n8n_base_url}
              onChange={(e) => setForm({ ...form, n8n_base_url: e.target.value })}
              placeholder="https://n8n.mi-dominio.com"
              className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            />
          </Field>
          <Field
            label="API Key"
            hint={settings?.n8n_api_key_set ? `guardada: ${settings.n8n_api_key_masked}` : undefined}
          >
            <Input
              type="password"
              data-testid={TID.settings.n8nKey}
              value={form.n8n_api_key}
              onChange={(e) => setForm({ ...form, n8n_api_key: e.target.value })}
              placeholder={settings?.n8n_api_key_set ? "Dejar vacío para conservar" : ""}
              className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            />
          </Field>
          <Field label="Webhook URL">
            <Input
              data-testid={TID.settings.n8nWebhook}
              value={form.n8n_webhook_url}
              onChange={(e) => setForm({ ...form, n8n_webhook_url: e.target.value })}
              placeholder="https://n8n.mi-dominio.com/webhook/..."
              className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            />
          </Field>
        </Section>

        <Section title="KRAI API">
          <Field label="Base URL">
            <Input
              data-testid={TID.settings.kraiBase}
              value={form.krai_api_base_url}
              onChange={(e) => setForm({ ...form, krai_api_base_url: e.target.value })}
              placeholder="https://api.krai.tu-dominio.com"
              className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            />
          </Field>
          <Field
            label="Token"
            hint={settings?.krai_api_token_set ? `guardado: ${settings.krai_api_token_masked}` : undefined}
          >
            <Input
              type="password"
              data-testid={TID.settings.kraiToken}
              value={form.krai_api_token}
              onChange={(e) => setForm({ ...form, krai_api_token: e.target.value })}
              placeholder={settings?.krai_api_token_set ? "Dejar vacío para conservar" : ""}
              className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            />
          </Field>
        </Section>

        <div className="flex justify-end">
          <Button
            type="submit"
            data-testid={TID.settings.save}
            disabled={saving}
            className="bg-white text-black hover:bg-zinc-200"
          >
            {saving ? "Guardando…" : "Guardar todo"}
          </Button>
        </div>
      </form>

      <div className="mt-8 max-w-3xl">
        <ChangePasswordCard />
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950 p-6">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-4">
        {title}
      </div>
      <div className="space-y-5">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-zinc-300 flex items-center gap-2">
        {label}
        {hint && <span className="text-[11px] font-mono text-emerald-400">{hint}</span>}
      </Label>
      {children}
    </div>
  );
}
